import React, { useState, useEffect, useCallback } from 'react';
import { Card, Tabs, Table, Tag, Button, Input, Switch, message, Statistic, Row, Col, Space, Modal, Form, Select, Popconfirm, Badge, Typography, Tooltip, Divider, InputNumber, Timeline } from 'antd';
import {
  CrownOutlined, DatabaseOutlined, TeamOutlined, SettingOutlined,
  MenuOutlined, HistoryOutlined, PlusOutlined, EditOutlined,
  DeleteOutlined, SaveOutlined, ReloadOutlined, UserOutlined,
  CheckCircleOutlined, CloseCircleOutlined, BarChartOutlined,
  ArrowUpOutlined, ArrowDownOutlined, AppstoreOutlined
} from '@ant-design/icons';
import api from '../utils/api';

const { TabPane } = Tabs;
const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const roleColors = {
  super_admin: 'red', admin: 'gold', manager: 'blue', hr_manager: 'purple',
  viewer: 'default', graphic_designer: 'cyan', video_editor: 'green',
  distribution_staff: 'orange', poster_viewer: 'lime', script_writer: 'magenta',
  audio_voiceover: 'volcano', youtube_manager: 'geekblue', content_researcher: 'purple', client: 'default'
};

const roleLabels = {
  super_admin: 'Super Admin', admin: 'Admin', manager: 'Manager', hr_manager: 'HR Manager',
  viewer: 'Viewer', graphic_designer: 'Graphic Designer', video_editor: 'Video Editor',
  distribution_staff: 'Distribution Staff', poster_viewer: 'Poster Viewer',
  script_writer: 'Script Writer', audio_voiceover: 'Audio Voiceover',
  youtube_manager: 'YouTube Manager', content_researcher: 'Content Researcher', client: 'Client'
};

export default function SuperAdminPanel({ user }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [menuConfig, setMenuConfig] = useState([]);
  const [systemConfig, setSystemConfig] = useState({});
  const [crons, setCrons] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(false);
  const [menuModal, setMenuModal] = useState(false);
  const [configModal, setConfigModal] = useState(false);
  const [editingMenu, setEditingMenu] = useState(null);
  const [form] = Form.useForm();
  const [configForm] = Form.useForm();

  const loadStats = useCallback(async () => {
    try {
      const res = await api.get('/super-admin/stats');
      setStats(res.data);
    } catch (e) { message.error('Failed to load stats'); }
  }, []);

  const loadAdmins = useCallback(async () => {
    try {
      const res = await api.get('/super-admin/admins');
      setAdmins(res.data);
    } catch (e) { message.error('Failed to load admins'); }
  }, []);

  const loadMenuConfig = useCallback(async () => {
    try {
      const res = await api.get('/super-admin/menu-config');
      setMenuConfig(res.data);
    } catch (e) { message.error('Failed to load menu config'); }
  }, []);

  const loadSystemConfig = useCallback(async () => {
    try {
      const res = await api.get('/super-admin/system-config');
      setSystemConfig(res.data.config || {});
      setCrons(res.data.crons || []);
    } catch (e) { message.error('Failed to load system config'); }
  }, []);

  const loadActivity = useCallback(async () => {
    try {
      const res = await api.get('/super-admin/recent-activity');
      setRecentActivity(res.data);
    } catch (e) { message.error('Failed to load activity'); }
  }, []);

  useEffect(() => {
    loadStats();
    loadAdmins();
  }, [loadStats, loadAdmins]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === 'menu') loadMenuConfig();
    if (key === 'system') loadSystemConfig();
    if (key === 'activity') loadActivity();
    if (key === 'admins') loadAdmins();
    if (key === 'overview') loadStats();
  };

  // Menu config handlers
  const handleMenuToggle = async (menuKey, enabled) => {
    try {
      await api.put('/super-admin/menu-config', { menu_key: menuKey, enabled: enabled ? 1 : 0 });
      message.success('Menu updated');
      loadMenuConfig();
    } catch (e) { message.error('Failed to update menu'); }
  };

  const handleMenuRename = async (menuKey, label) => {
    try {
      await api.put('/super-admin/menu-config', { menu_key: menuKey, label });
      message.success('Menu renamed');
      loadMenuConfig();
    } catch (e) { message.error('Failed to rename menu'); }
  };

  const handleMenuReorder = async (menuKey, direction) => {
    const idx = menuConfig.findIndex(m => m.menu_key === menuKey);
    if (idx < 0) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= menuConfig.length) return;
    const currentOrder = menuConfig[idx].sort_order;
    const swapOrder = menuConfig[swapIdx].sort_order;
    try {
      await api.put('/super-admin/menu-config', { menu_key: menuConfig[idx].menu_key, sort_order: swapOrder });
      await api.put('/super-admin/menu-config', { menu_key: menuConfig[swapIdx].menu_key, sort_order: currentOrder });
      message.success('Order updated');
      loadMenuConfig();
    } catch (e) { message.error('Failed to reorder'); }
  };

  const handleAddMenu = async (values) => {
    try {
      await api.post('/super-admin/menu-config', values);
      message.success('Menu item added');
      setMenuModal(false);
      form.resetFields();
      loadMenuConfig();
    } catch (e) { message.error('Failed to add menu'); }
  };

  const handleDeleteMenu = async (menuKey) => {
    try {
      await api.delete(`/super-admin/menu-config/${menuKey}`);
      message.success('Menu item deleted');
      loadMenuConfig();
    } catch (e) { message.error('Failed to delete menu'); }
  };

  const handleSaveConfig = async (values) => {
    try {
      await api.put('/super-admin/system-config', { key: values.key, value: values.value });
      message.success('Config saved');
      setConfigModal(false);
      configForm.resetFields();
      loadSystemConfig();
    } catch (e) { message.error('Failed to save config'); }
  };

  // =====================
  // OVERVIEW TAB
  // =====================
  const OverviewTab = () => (
    <div>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card style={{ background: 'linear-gradient(135deg, #ff4d4f, #ff7a45)', border: 'none' }}>
            <Row align="middle" gutter={16}>
              <Col>
                <CrownOutlined style={{ fontSize: 48, color: '#fff' }} />
              </Col>
              <Col>
                <Title level={3} style={{ color: '#fff', margin: 0 }}>Super Admin Control Panel</Title>
                <Text style={{ color: '#ffffffcc' }}>Full system control — manage admins, menu, settings, and monitor everything</Text>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {stats && (
        <>
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={12} sm={8} md={6} lg={4}>
              <Card size="small"><Statistic title="Total Users" value={stats.stats?.find(s => s.table === 'users')?.count || 0} prefix={<UserOutlined />} /></Card>
            </Col>
            <Col xs={12} sm={8} md={6} lg={4}>
              <Card size="small"><Statistic title="Songs" value={stats.stats?.find(s => s.table === 'songs')?.count || 0} valueStyle={{ color: '#3f8600' }} /></Card>
            </Col>
            <Col xs={12} sm={8} md={6} lg={4}>
              <Card size="small"><Statistic title="Channels" value={stats.stats?.find(s => s.table === 'youtube_channels')?.count || 0} valueStyle={{ color: '#cf1322' }} /></Card>
            </Col>
            <Col xs={12} sm={8} md={6} lg={4}>
              <Card size="small"><Statistic title="Thumbnails" value={stats.stats?.find(s => s.table === 'thumbnails')?.count || 0} valueStyle={{ color: '#1890ff' }} /></Card>
            </Col>
            <Col xs={12} sm={8} md={6} lg={4}>
              <Card size="small"><Statistic title="Tasks" value={stats.stats?.find(s => s.table === 'staff_tasks')?.count || 0} valueStyle={{ color: '#722ed1' }} /></Card>
            </Col>
            <Col xs={12} sm={8} md={6} lg={4}>
              <Card size="small"><Statistic title="Activity (24h)" value={stats.recent_activity_24h || 0} valueStyle={{ color: '#fa8c16' }} /></Card>
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} md={12}>
              <Card title={<><TeamOutlined /> Role Breakdown</>} size="small">
                <Table
                  dataSource={stats.roles || []}
                  columns={[
                    { title: 'Role', dataIndex: 'role', render: r => <Tag color={roleColors[r] || 'default'}>{roleLabels[r] || r}</Tag> },
                    { title: 'Users', dataIndex: 'count', render: c => <Badge count={c} style={{ backgroundColor: '#52c41a' }} overflowCount={999} /> }
                  ]}
                  pagination={false}
                  rowKey="role"
                  size="small"
                />
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card title={<><DatabaseOutlined /> Database Tables</>} size="small">
                <Table
                  dataSource={stats.stats || []}
                  columns={[
                    { title: 'Table', dataIndex: 'label' },
                    { title: 'Records', dataIndex: 'count', render: (c, r) => r.error ? <Tag color="red">Error</Tag> : <Text strong>{c.toLocaleString()}</Text> }
                  ]}
                  pagination={false}
                  rowKey="table"
                  size="small"
                  scroll={{ y: 300 }}
                />
              </Card>
            </Col>
          </Row>
        </>
      )}
    </div>
  );

  // =====================
  // ADMIN MANAGEMENT TAB
  // =====================
  const AdminsTab = () => {
    const adminCols = [
      { title: 'ID', dataIndex: 'id', width: 50 },
      { title: 'Name', dataIndex: 'full_name', render: (n, r) => (
        <Space>
          <Text strong>{n || r.username}</Text>
          {r.role === 'super_admin' && <CrownOutlined style={{ color: '#ff4d4f' }} />}
        </Space>
      )},
      { title: 'Username', dataIndex: 'username' },
      { title: 'Email', dataIndex: 'email', ellipsis: true },
      { title: 'Role', dataIndex: 'role', render: r => <Tag color={roleColors[r]}>{roleLabels[r] || r}</Tag> },
      { title: 'Staff ID', dataIndex: 'staff_id', render: id => id || '-' },
      { title: 'Status', dataIndex: 'is_approved', render: a => a ? <Tag color="green"><CheckCircleOutlined /> Active</Tag> : <Tag color="red"><CloseCircleOutlined /> Inactive</Tag> },
      { title: 'Created', dataIndex: 'created_at', render: d => d ? new Date(d).toLocaleDateString('en-IN') : '-' },
      { title: 'Permissions', dataIndex: 'permissions', render: p => <Text type="secondary">{Array.isArray(p) ? p.length : 0} perms</Text> },
    ];

    return (
      <Card title={<><TeamOutlined /> Admin-Level Users</>} extra={<Button icon={<ReloadOutlined />} onClick={loadAdmins}>Refresh</Button>}>
        <Table
          dataSource={admins}
          columns={adminCols}
          rowKey="id"
          size="small"
          scroll={{ x: 900 }}
          pagination={false}
        />
        <Text type="secondary" style={{ marginTop: 8, display: 'block' }}>
          Admins ko manage karne ke liye "Users & Log" page pe jaake edit/delete karo. Super Admin ka role sirf Super Admin hi change kar sakta hai.
        </Text>
      </Card>
    );
  };

  // =====================
  // MENU CONTROL TAB
  // =====================
  const MenuTab = () => {
    const [editLabel, setEditLabel] = useState({});

    const menuCols = [
      { title: '#', render: (_, __, i) => i + 1, width: 40 },
      { title: 'Key', dataIndex: 'menu_key', width: 150, render: k => <Tag>{k}</Tag> },
      { title: 'Label', dataIndex: 'label', render: (l, r) => (
        <Space>
          <Input
            size="small"
            defaultValue={l}
            style={{ width: 200 }}
            onChange={(e) => setEditLabel(prev => ({ ...prev, [r.menu_key]: e.target.value }))}
            onPressEnter={() => {
              const val = editLabel[r.menu_key];
              if (val && val !== l) handleMenuRename(r.menu_key, val);
            }}
          />
          {editLabel[r.menu_key] && editLabel[r.menu_key] !== l && (
            <Button size="small" type="primary" icon={<SaveOutlined />} onClick={() => handleMenuRename(r.menu_key, editLabel[r.menu_key])} />
          )}
        </Space>
      )},
      { title: 'Enabled', dataIndex: 'enabled', width: 80, render: (e, r) => (
        <Switch checked={!!e} onChange={(v) => handleMenuToggle(r.menu_key, v)} />
      )},
      { title: 'Order', width: 100, render: (_, r, i) => (
        <Space>
          <Button size="small" icon={<ArrowUpOutlined />} disabled={i === 0} onClick={() => handleMenuReorder(r.menu_key, 'up')} />
          <Button size="small" icon={<ArrowDownOutlined />} disabled={i === menuConfig.length - 1} onClick={() => handleMenuReorder(r.menu_key, 'down')} />
        </Space>
      )},
      { title: 'Actions', width: 80, render: (_, r) => (
        <Popconfirm title="Delete this menu item?" onConfirm={() => handleDeleteMenu(r.menu_key)}>
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      )}
    ];

    return (
      <Card
        title={<><MenuOutlined /> Sidebar Menu Control</>}
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadMenuConfig}>Refresh</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setMenuModal(true)}>Add Menu Item</Button>
          </Space>
        }
      >
        <Table dataSource={menuConfig} columns={menuCols} rowKey="menu_key" size="small" pagination={false} />

        <Modal title="Add New Menu Item" open={menuModal} onCancel={() => setMenuModal(false)} footer={null}>
          <Form form={form} onFinish={handleAddMenu} layout="vertical">
            <Form.Item name="menu_key" label="Menu Key (unique ID)" rules={[{ required: true }]}>
              <Input placeholder="e.g. my-custom-page" />
            </Form.Item>
            <Form.Item name="label" label="Display Label" rules={[{ required: true }]}>
              <Input placeholder="e.g. My Custom Page" />
            </Form.Item>
            <Form.Item name="icon" label="Icon" initialValue="AppstoreOutlined">
              <Select>
                <Option value="AppstoreOutlined">AppstoreOutlined</Option>
                <Option value="BarChartOutlined">BarChartOutlined</Option>
                <Option value="FileOutlined">FileOutlined</Option>
                <Option value="SettingOutlined">SettingOutlined</Option>
                <Option value="UserOutlined">UserOutlined</Option>
                <Option value="DatabaseOutlined">DatabaseOutlined</Option>
                <Option value="LinkOutlined">LinkOutlined</Option>
              </Select>
            </Form.Item>
            <Button type="primary" htmlType="submit" block>Add Menu Item</Button>
          </Form>
        </Modal>
      </Card>
    );
  };

  // =====================
  // SYSTEM CONFIG TAB
  // =====================
  const SystemTab = () => (
    <Row gutter={[16, 16]}>
      <Col xs={24} md={12}>
        <Card
          title={<><SettingOutlined /> System Configuration</>}
          extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => setConfigModal(true)}>Add Config</Button>}
        >
          {Object.keys(systemConfig).length === 0 ? (
            <Text type="secondary">No configuration found. Add system settings like site name, email settings, etc.</Text>
          ) : (
            <Table
              dataSource={Object.entries(systemConfig).map(([k, v]) => ({ key: k, value: v }))}
              columns={[
                { title: 'Key', dataIndex: 'key', render: k => <Tag color="blue">{k}</Tag> },
                { title: 'Value', dataIndex: 'value', ellipsis: true },
                { title: 'Actions', width: 60, render: (_, r) => (
                  <Button size="small" icon={<EditOutlined />} onClick={() => {
                    configForm.setFieldsValue({ key: r.key, value: r.value });
                    setConfigModal(true);
                  }} />
                )}
              ]}
              rowKey="key"
              size="small"
              pagination={false}
            />
          )}

          <Modal title="System Config" open={configModal} onCancel={() => { setConfigModal(false); configForm.resetFields(); }} footer={null}>
            <Form form={configForm} onFinish={handleSaveConfig} layout="vertical">
              <Form.Item name="key" label="Config Key" rules={[{ required: true }]}>
                <Input placeholder="e.g. site_name, admin_email, etc." />
              </Form.Item>
              <Form.Item name="value" label="Value" rules={[{ required: true }]}>
                <TextArea rows={3} placeholder="Config value" />
              </Form.Item>
              <Button type="primary" htmlType="submit" block icon={<SaveOutlined />}>Save Config</Button>
            </Form>
          </Modal>
        </Card>
      </Col>
      <Col xs={24} md={12}>
        <Card title={<><HistoryOutlined /> Active Cron Jobs</>}>
          {crons.length === 0 ? (
            <Text type="secondary">No cron jobs found</Text>
          ) : (
            <Timeline>
              {crons.map((c, i) => (
                <Timeline.Item key={i} color="blue">
                  <Text code style={{ fontSize: 12, wordBreak: 'break-all' }}>{c}</Text>
                </Timeline.Item>
              ))}
            </Timeline>
          )}
        </Card>
      </Col>
    </Row>
  );

  // =====================
  // ACTIVITY LOG TAB
  // =====================
  const ActivityTab = () => {
    const actCols = [
      { title: 'Time', dataIndex: 'created_at', width: 150, render: d => d ? new Date(d).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-' },
      { title: 'User', render: (_, r) => r.full_name || r.username || `ID:${r.user_id}` },
      { title: 'Action', dataIndex: 'action', render: a => <Tag color="blue">{a}</Tag> },
      { title: 'Entity', dataIndex: 'entity_type', render: e => e || '-' },
      { title: 'Details', dataIndex: 'details', ellipsis: true, render: d => d || '-' },
    ];

    return (
      <Card
        title={<><HistoryOutlined /> Recent Activity (Last 100)</>}
        extra={<Button icon={<ReloadOutlined />} onClick={loadActivity}>Refresh</Button>}
      >
        <Table
          dataSource={recentActivity}
          columns={actCols}
          rowKey={(r, i) => `${r.id || i}`}
          size="small"
          scroll={{ x: 800 }}
          pagination={{ pageSize: 20 }}
        />
      </Card>
    );
  };

  return (
    <div style={{ padding: '0 0 24px' }}>
      <Card
        style={{ marginBottom: 16, background: 'linear-gradient(135deg, #1a1a2e, #16213e)', border: 'none' }}
        bodyStyle={{ padding: '16px 24px' }}
      >
        <Row align="middle" justify="space-between">
          <Col>
            <Space align="center">
              <CrownOutlined style={{ fontSize: 32, color: '#ff4d4f' }} />
              <div>
                <Title level={4} style={{ color: '#fff', margin: 0 }}>Super Admin Panel</Title>
                <Text style={{ color: '#ffffffaa' }}>Welcome, {user?.full_name || user?.username} — You have full system control</Text>
              </div>
            </Space>
          </Col>
          <Col>
            <Tag color="red" style={{ fontSize: 14, padding: '4px 12px' }}>
              <CrownOutlined /> SUPER ADMIN
            </Tag>
          </Col>
        </Row>
      </Card>

      <Tabs activeKey={activeTab} onChange={handleTabChange} type="card" size="large">
        <TabPane tab={<><BarChartOutlined /> Overview</>} key="overview">
          <OverviewTab />
        </TabPane>
        <TabPane tab={<><TeamOutlined /> Admin Management</>} key="admins">
          <AdminsTab />
        </TabPane>
        <TabPane tab={<><MenuOutlined /> Menu Control</>} key="menu">
          <MenuTab />
        </TabPane>
        <TabPane tab={<><SettingOutlined /> System Config</>} key="system">
          <SystemTab />
        </TabPane>
        <TabPane tab={<><HistoryOutlined /> Activity Monitor</>} key="activity">
          <ActivityTab />
        </TabPane>
      </Tabs>
    </div>
  );
}
